/*
 * File: amplitude.ts
 * Project: meki
 * Description: Amplitude Browser SDK 2 initialization and configuration
 */

import * as amplitude from '@amplitude/analytics-browser';
import { NEXT_PUBLIC_AMPLITUDE_API_KEY } from '@config/environment';
import { isUserFromChile, isUserFromChileSync } from '@utils/ipGeolocation';
import { Console } from '@utils/logger';

export const preloadCountryDetection = async () => {
  try {
    await isUserFromChile();
    Console.log(
      '[AMPLITUDE]:Country detection preloaded for Amplitude tracking',
    );
  } catch (error) {
    Console.warn('[AMPLITUDE]: Failed to preload country detection:', error);
  }
};

export const initializeAmplitude = () => {
  if (!NEXT_PUBLIC_AMPLITUDE_API_KEY) {
    Console.warn(
      '[AMPLITUDE]: Amplitude API key not found. Analytics tracking disabled.',
    );
    return;
  }
  try {
    amplitude.init(NEXT_PUBLIC_AMPLITUDE_API_KEY, {
      autocapture: {
        pageViews: {
          trackOn: () => {
            const cachedResult = isUserFromChileSync();
            if (cachedResult !== null) {
              Console.log(
                '[AMPLITUDE]: Amplitude pageView tracking (cached):',
                cachedResult ? 'enabled (Chile)' : 'blocked (non-Chile)',
              );
              return cachedResult;
            }
            isUserFromChile()
              .then((isFromChile) => {
                Console.log(
                  '[AMPLITUDE]: Amplitude pageView tracking (async):',
                  isFromChile ? 'enabled (Chile)' : 'blocked (non-Chile)',
                );
                return isFromChile;
              })
              .catch((error) => {
                Console.warn(
                  '[AMPLITUDE]: Failed to check user location for Amplitude tracking:',
                  error,
                );
                return true;
              });
            Console.log(
              '[AMPLITUDE]: Amplitude pageView tracking (async): blocked',
            );
            return false;
          },
        },
        attribution: true,
        sessions: true,
        formInteractions: true,
        fileDownloads: true,
        elementInteractions: true,
        networkTracking: true,
      },
    });

    Console.log('[AMPLITUDE]: Amplitude initialized successfully');
  } catch (error) {
    Console.error('[AMPLITUDE]: Failed to initialize Amplitude:', error);
  }
};

export const identifyUser = (
  userId: string,
  userProperties?: Record<string, any>,
) => {
  try {
    if (userProperties) {
      const identify = new amplitude.Identify();
      Object.entries(userProperties).forEach(([key, value]) => {
        identify.set(key, value);
      });
      amplitude.identify(identify);
    }

    amplitude.setUserId(userId);
    Console.log('[AMPLITUDE]: User identified in Amplitude:', userId);
  } catch (error) {
    Console.error('[AMPLITUDE]: Failed to identify user in Amplitude:', error);
  }
};

export const trackEvent = async (
  eventName: string,
  eventProperties?: Record<string, any>,
) => {
  try {
    let isFromChile = isUserFromChileSync();
    if (isFromChile === null) {
      isFromChile = await isUserFromChile();
    }
    if (isFromChile) {
      amplitude.track(eventName, eventProperties);
    } else {
      Console.log(
        '[AMPLITUDE]: Event not tracked because user is not from Chile',
        eventName,
        eventProperties,
        isFromChile,
      );
    }
  } catch (error) {
    Console.error('[AMPLITUDE]: Failed to track event in Amplitude:', error);
  }
};

export const resetAmplitude = () => {
  try {
    amplitude.reset();
    Console.log('[AMPLITUDE]: Amplitude user data reset');
  } catch (error) {
    Console.error('[AMPLITUDE]: Failed to reset Amplitude:', error);
  }
};

export const getAmplitudeInstance = () => amplitude;

export default amplitude;
